import React from 'react';

import {
  AdminPanelSettings as AdminPanelSettingsIcon,
  BorderColor as BorderColorIcon,
  Delete as DeleteFilledIcon,
  Redo as RedoIcon,
} from '@mui/icons-material';
import { useTheme } from '@mui/material';

import { STATUSES_FILTER_OPTIONS } from '@clearverify/constants';
import { formatPhoneNumber, parseNullableValue } from '@clearverify/utils';

import {
  EmployerStaffInfoFragment,
  UserStatus,
  useResendInviteToEmployerStaffMutation,
} from 'src/api';
import { ClickableLabel, UserStatusChip } from 'src/components/common';
import { DemoPrivacyText } from 'src/components/demoPrivacyMode';
import { Table, TableColumn, TableFilter, TableRowAction, TableThemes } from 'src/components/table';
import { ModalId } from 'src/constants/modals';
import { BusinessRole } from 'src/constants/user';
import { useNotification } from 'src/hooks/app';
import { useFilterStorage, useSearch } from 'src/hooks/common';
import { useModal, useModalV2 } from 'src/hooks/shared';
import { useCurrentUserPermissions } from 'src/hooks/user';
import { MfaManagementModal } from 'src/modals/auth';
import { DeleteEmployerStaffConfirmationModal } from 'src/modals/employerStaff';
import { ResendInviteModalArgs } from 'src/modals/invite';
import { PaginationContextValue } from 'src/providers/PaginationProvider';
import { hasOneOfRoles } from 'src/utils/role';

export interface EmployerStaffTableProps {
  employerId?: string;
  employerStaffList?: EmployerStaffInfoFragment[];
  employerStaffCount?: number;
  pagination: PaginationContextValue;
  onRefresh: () => Promise<void>;
  loading: boolean;
}

export const EmployerStaffTable: React.FC<EmployerStaffTableProps> = ({
  employerId,
  employerStaffList = [],
  employerStaffCount = 0,
  pagination,
  onRefresh,
  loading,
}) => {
  const { createApolloContext } = useNotification();

  const theme = useTheme();

  const { openModal } = useModal();
  const { openModal: openModalV2 } = useModalV2();

  const { setSearchValue, searchValue } = useSearch();
  const { setSortOption } = useFilterStorage();

  const {
    hasPlatformRoles,
    isPlatformAdmin,
    isEmployerAdmin,
    isPeoAdmin,
    isPeoPayrollProviderUser,
  } = useCurrentUserPermissions();

  const [resendInviteToEmployerStaff] = useResendInviteToEmployerStaffMutation({
    context: createApolloContext({
      successMessage: 'Invite has been resent successfully',
    }),
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies:useEffect must not depend on "pagination" here
  React.useEffect(() => {
    pagination.resetPage();
  }, [searchValue]);

  const rowActions = React.useMemo(() => {
    const actions: Array<TableRowAction<EmployerStaffInfoFragment>> = [
      {
        startIcon: <BorderColorIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'View Details',
        onAction: staff => {
          openModal(ModalId.EmployerStaffDetailsModal, {
            employerStaffId: staff.id,
            employerId,
          });
        },
      },
    ];

    if (hasPlatformRoles) {
      actions.push({
        startIcon: <AdminPanelSettingsIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'MFA Management',
        onAction: staff => {
          openModalV2(MfaManagementModal, { id: ModalId.MfaManagement, userId: staff.user.id });
        },
      });
    }

    if (isPlatformAdmin) {
      actions.push({
        startIcon: <DeleteFilledIcon sx={{ margin: '0 10px 0 0', color: 'error.main' }} />,
        actionName: 'Delete Staff',
        style: { color: theme.palette.error.main },
        onAction: staff => {
          openModalV2(DeleteEmployerStaffConfirmationModal, {
            id: ModalId.DeleteEmployerStaffConfirmationModal,
            employerStaffId: staff.id,
          });
        },
      });
    }

    if (hasPlatformRoles || isEmployerAdmin || isPeoAdmin || isPeoPayrollProviderUser) {
      actions.push({
        startIcon: <RedoIcon style={{ margin: '0 10px 0 0' }} />,
        actionName: 'Resend Invite',
        hidden: staff => staff.user.status !== UserStatus.Pending,
        onAction: staff => {
          const args: ResendInviteModalArgs = {
            toStaff: 'Employer',
            staffEmail: staff.user.email,
            onSubmit: async () => {
              const { data } = await resendInviteToEmployerStaff({
                variables: { employerStaffId: staff.id },
              });
              return data?.resendInviteToEmployerStaff;
            },
          };

          openModal(ModalId.ResendInvite, args);
        },
      });
    }

    return actions;
  }, [
    hasPlatformRoles,
    isPlatformAdmin,
    isEmployerAdmin,
    isPeoAdmin,
    isPeoPayrollProviderUser,
    openModal,
    openModalV2,
    resendInviteToEmployerStaff,
    employerId,
    theme,
  ]);

  return (
    <Table
      dataSource={employerStaffList}
      rowCount={employerStaffCount}
      columns={COLUMNS}
      pagination={pagination}
      onSearch={setSearchValue}
      loading={loading}
      rowActions={rowActions}
      filters={filters}
      tableTheme={TableThemes.withBorderWithoutProfileIcon}
      onRefresh={onRefresh}
      onSort={setSortOption}
    />
  );
};

const COLUMNS: TableColumn<EmployerStaffInfoFragment>[] = [
  {
    title: 'Full Name',
    key: 'user.firstName',
    sortable: true,
    render: (_valueByDataPath, employerStaff, { rowActions }) => {
      const isEmployerAdmin = hasOneOfRoles(employerStaff, [BusinessRole.EmployerAdmin]);
      return (
        <ClickableLabel
          type="button"
          onClick={() => rowActions?.[0]?.onAction(employerStaff)}
          label={employerStaff.user.fullName}
          labelPostfix={isEmployerAdmin ? 'Admin' : undefined}
          logoUrl={employerStaff.user.avatar?.downloadUrl}
        />
      );
    },
  },
  {
    title: 'Phone Number',
    key: 'user.phoneNumber',
    render: (_valueByDataPath, employerStaff) => (
      <DemoPrivacyText>
        {parseNullableValue(formatPhoneNumber(employerStaff.user.phoneNumber))}
      </DemoPrivacyText>
    ),
  },
  {
    title: 'Email',
    key: 'user.email',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, employerStaff) => (
      <DemoPrivacyText>{parseNullableValue(employerStaff.user.email)}</DemoPrivacyText>
    ),
  },
  {
    title: 'Status',
    key: 'user.status',
    sortable: true,
    nonNullSortable: true,
    render: (_valueByDataPath, employerStaff) => (
      <UserStatusChip userStatus={employerStaff.user.status} />
    ),
  },
];

const filters: TableFilter[] = [
  {
    id: 'status',
    type: 'select',
    fieldProps: {
      label: 'Status',
      multiple: true,
      options: STATUSES_FILTER_OPTIONS,
    },
  },
];
